import { Decimal } from "decimal.js";
import { z } from "zod";

// ─────────────────────────────── Cede Arena tournaments ───────────────────────
//
// A multi-round tournament (the $100K). Round 1 is open to every participant; each
// later round only to the previous round's qualifiers; the last round ranks the
// winners. Without a whitelist a player becomes a participant with their first counted
// duel before joining closes; with one, by joining. A round ranks either
// on gross paid-duel winnings in its window or on the final leaderboard of one linked
// multi-player competition. Two side boards (Sniper: win rate, Streak: longest run of
// wins) span the whole tournament. The prize pool is a step ladder keyed on the buy-ins of
// the scored duels: every player's under automatic enrollment, else the participants'.
//
// Counted duel: a finalized matchmaking duel (Classic or Spin & Go) with a positive
// buy-in, inside the tournament, not voided by ops, and finalized after the player
// joined unless they were enrolled automatically.

export const ARENA_TOURNAMENT_SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const ARENA_TOURNAMENT_SLUG_MAX = 64;
export const ARENA_TOURNAMENT_NAME_MAX = 80;
export const ARENA_TOURNAMENT_ROUND_NAME_MAX = 40;
export const ARENA_TOURNAMENT_MAX_ROUNDS = 10;
export const ARENA_TOURNAMENT_MAX_PRIZE_TIERS = 30;
export const ARENA_TOURNAMENT_MAX_PLACE_BRACKETS = 20;
/** A week: a longer grace window is a typo, and prizes would sit unpaid. */
export const ARENA_TOURNAMENT_MAX_PAYOUT_GRACE_MINUTES = 7 * 24 * 60;
export const ARENA_TOURNAMENT_DISQUALIFY_REASON_MAX = 280;
export const ARENA_TOURNAMENT_VOID_REASON_MAX = 280;
export const ARENA_TOURNAMENT_PAGE_SIZE_MAX = 100;
export const ARENA_TOURNAMENT_PARTICIPANT_SEARCH_MAX = 64;
/** A single-match round opens this long after the cut, so its qualifiers are whitelisted before it starts. */
export const ARENA_TOURNAMENT_MATCH_MIN_GAP_MS = 10 * 60_000;

/** A non-negative USDT decimal string (up to 6 dp, the ledger's precision). */
export const ARENA_TOURNAMENT_USDT_REGEX = /^(?:0|[1-9]\d*)(?:\.\d{1,6})?$/;
const UsdtSchema = z.string().regex(ARENA_TOURNAMENT_USDT_REGEX, {
  error: "must be a non-negative decimal with up to 6 decimals",
});
const PositiveUsdtSchema = UsdtSchema.refine(
  (value) => !ARENA_TOURNAMENT_USDT_REGEX.test(value) || new Decimal(value).gt(0),
  { error: "must be greater than 0" },
);

export const ARENA_TOURNAMENT_ROUND_FORMAT = {
  /** Gross prizes won in counted duels settled inside the round window, like the weekly mixed board. */
  WINNINGS: "WINNINGS",
  /** The final leaderboard of one linked multi-player competition, entered by the previous round's qualifiers. */
  SINGLE_MATCH: "SINGLE_MATCH",
} as const;
export type ARENA_TOURNAMENT_ROUND_FORMAT =
  (typeof ARENA_TOURNAMENT_ROUND_FORMAT)[keyof typeof ARENA_TOURNAMENT_ROUND_FORMAT];
export const ArenaTournamentRoundFormatSchema = z.enum([
  ARENA_TOURNAMENT_ROUND_FORMAT.WINNINGS,
  ARENA_TOURNAMENT_ROUND_FORMAT.SINGLE_MATCH,
]);

/** Derived from the round dates, the freeze stamps and `paidAt`; never stored. */
export const ARENA_TOURNAMENT_STATUS = {
  UPCOMING: "UPCOMING",
  LIVE: "LIVE",
  /** The last round ended; admins can still disqualify or void until the payout runs. */
  AWAITING_PAYOUT: "AWAITING_PAYOUT",
  COMPLETED: "COMPLETED",
} as const;
export type ARENA_TOURNAMENT_STATUS = (typeof ARENA_TOURNAMENT_STATUS)[keyof typeof ARENA_TOURNAMENT_STATUS];
export const ArenaTournamentStatusSchema = z.enum([
  ARENA_TOURNAMENT_STATUS.UPCOMING,
  ARENA_TOURNAMENT_STATUS.LIVE,
  ARENA_TOURNAMENT_STATUS.AWAITING_PAYOUT,
  ARENA_TOURNAMENT_STATUS.COMPLETED,
]);

export const ARENA_TOURNAMENT_ROUND_STATUS = {
  UPCOMING: "UPCOMING",
  LIVE: "LIVE",
  /** The window closed; the cut is not frozen yet (a linked match still settling, or the freeze job pending). */
  CLOSING: "CLOSING",
  CLOSED: "CLOSED",
} as const;
export type ARENA_TOURNAMENT_ROUND_STATUS =
  (typeof ARENA_TOURNAMENT_ROUND_STATUS)[keyof typeof ARENA_TOURNAMENT_ROUND_STATUS];
export const ArenaTournamentRoundStatusSchema = z.enum([
  ARENA_TOURNAMENT_ROUND_STATUS.UPCOMING,
  ARENA_TOURNAMENT_ROUND_STATUS.LIVE,
  ARENA_TOURNAMENT_ROUND_STATUS.CLOSING,
  ARENA_TOURNAMENT_ROUND_STATUS.CLOSED,
]);

export const ARENA_TOURNAMENT_PRIZE_CATEGORY = {
  PLACE: "PLACE",
  SNIPER: "SNIPER",
  STREAK: "STREAK",
} as const;
export type ARENA_TOURNAMENT_PRIZE_CATEGORY =
  (typeof ARENA_TOURNAMENT_PRIZE_CATEGORY)[keyof typeof ARENA_TOURNAMENT_PRIZE_CATEGORY];
export const ArenaTournamentPrizeCategorySchema = z.enum([
  ARENA_TOURNAMENT_PRIZE_CATEGORY.PLACE,
  ARENA_TOURNAMENT_PRIZE_CATEGORY.SNIPER,
  ARENA_TOURNAMENT_PRIZE_CATEGORY.STREAK,
]);

export const ARENA_TOURNAMENT_SIDE_BOARD = {
  SNIPER: "SNIPER",
  STREAK: "STREAK",
} as const;
export type ARENA_TOURNAMENT_SIDE_BOARD =
  (typeof ARENA_TOURNAMENT_SIDE_BOARD)[keyof typeof ARENA_TOURNAMENT_SIDE_BOARD];
export const ArenaTournamentSideBoardSchema = z.enum([
  ARENA_TOURNAMENT_SIDE_BOARD.SNIPER,
  ARENA_TOURNAMENT_SIDE_BOARD.STREAK,
]);

// ─────────────────────────────── Prize ladder ─────────────────────────────────

/** Every rank in `[rankFrom, rankTo]` of the last round is paid `amountUsdt` each. */
export const ArenaTournamentPlacePrizeSchema = z.object({
  rankFrom: z.number().int().positive(),
  rankTo: z.number().int().positive(),
  amountUsdt: PositiveUsdtSchema,
});
export type ArenaTournamentPlacePrize = z.infer<typeof ArenaTournamentPlacePrizeSchema>;

const placesParse = (places: readonly ArenaTournamentPlacePrize[]): boolean =>
  places.every((place) => ARENA_TOURNAMENT_USDT_REGEX.test(place.amountUsdt));

/** Places paid by a tier: contiguous brackets from rank 1, e.g. 1, 2, 3, 4, 5–8. */
export const ArenaTournamentPlacePrizesSchema = z
  .array(ArenaTournamentPlacePrizeSchema)
  .min(1)
  .max(ARENA_TOURNAMENT_MAX_PLACE_BRACKETS)
  .refine((places) => places.every((place) => place.rankTo >= place.rankFrom), {
    error: "a bracket's last rank must not be below its first",
  })
  .refine((places) => places.every((place, i) => place.rankFrom === (places[i - 1]?.rankTo ?? 0) + 1), {
    error: "place brackets must start at rank 1 and follow each other without gaps",
  });

/** What the places of a tier pay in total, every rank filled. */
export function arenaTournamentPlacesTotal(places: readonly ArenaTournamentPlacePrize[]): Decimal {
  return places.reduce(
    (total, place) => total.plus(new Decimal(place.amountUsdt).times(place.rankTo - place.rankFrom + 1)),
    new Decimal(0),
  );
}

/**
 * One row of the ladder: once participants' counted buy-ins reach `thresholdUsdt`,
 * the pool is `poolUsdt`, paid out as `places` + one Sniper + one Streak prize.
 */
export const ArenaTournamentPrizeTierSchema = z
  .object({
    thresholdUsdt: UsdtSchema,
    poolUsdt: PositiveUsdtSchema,
    places: ArenaTournamentPlacePrizesSchema,
    sniperUsdt: UsdtSchema,
    streakUsdt: UsdtSchema,
  })
  .refine(
    (tier) =>
      !placesParse(tier.places) ||
      ![tier.poolUsdt, tier.sniperUsdt, tier.streakUsdt].every((value) => ARENA_TOURNAMENT_USDT_REGEX.test(value)) ||
      arenaTournamentPlacesTotal(tier.places).plus(tier.sniperUsdt).plus(tier.streakUsdt).eq(tier.poolUsdt),
    { error: "places + Sniper + Streak must add up to the tier's pool" },
  );
export type ArenaTournamentPrizeTier = z.infer<typeof ArenaTournamentPrizeTierSchema>;

const amountsParse = (tiers: readonly ArenaTournamentPrizeTier[]): boolean =>
  tiers.every(
    (tier) => ARENA_TOURNAMENT_USDT_REGEX.test(tier.thresholdUsdt) && ARENA_TOURNAMENT_USDT_REGEX.test(tier.poolUsdt),
  );

export const ArenaTournamentPrizeLadderSchema = z
  .array(ArenaTournamentPrizeTierSchema)
  .min(1)
  .max(ARENA_TOURNAMENT_MAX_PRIZE_TIERS)
  .refine(
    (tiers) =>
      !amountsParse(tiers) ||
      tiers.every((tier, i) => {
        const previous = tiers[i - 1];
        return (
          previous === undefined ||
          (new Decimal(tier.thresholdUsdt).gt(previous.thresholdUsdt) &&
            new Decimal(tier.poolUsdt).gt(previous.poolUsdt))
        );
      }),
    { error: "tiers must go strictly up in both buy-in threshold and pool" },
  );
export type ArenaTournamentPrizeLadder = z.infer<typeof ArenaTournamentPrizeLadderSchema>;

/** Index of the highest tier whose threshold `volumeUsdt` reached, or null below the first one. */
export function arenaTournamentReachedTierIndex(
  ladder: readonly ArenaTournamentPrizeTier[],
  volumeUsdt: Decimal.Value,
): number | null {
  let reached: number | null = null;
  ladder.forEach((tier, i) => {
    if (new Decimal(volumeUsdt).gte(tier.thresholdUsdt)) reached = i;
  });
  return reached;
}

/** What `rank` of the last round is paid under `tier`, or null when no bracket covers it. */
export function arenaTournamentPlacePrizeForRank(tier: ArenaTournamentPrizeTier, rank: number): string | null {
  return tier.places.find((place) => rank >= place.rankFrom && rank <= place.rankTo)?.amountUsdt ?? null;
}

// ─────────────────────────────── CMS config ───────────────────────────────────

export const ArenaTournamentRoundConfigSchema = z.object({
  name: z.string().trim().min(1).max(ARENA_TOURNAMENT_ROUND_NAME_MAX),
  startsAt: z.coerce.date(),
  endsAt: z.coerce.date(),
  format: ArenaTournamentRoundFormatSchema,
  /** How many move on to the next round. Null on the last round, which ranks the winners instead. */
  qualifiersCount: z.number().int().positive().nullable(),
});
export type ArenaTournamentRoundConfig = z.infer<typeof ArenaTournamentRoundConfigSchema>;

export const ArenaTournamentRoundsConfigSchema = z
  .array(ArenaTournamentRoundConfigSchema)
  .min(1)
  .max(ARENA_TOURNAMENT_MAX_ROUNDS)
  .refine((rounds) => rounds.every((round) => round.endsAt.getTime() > round.startsAt.getTime()), {
    error: "a round must end after it starts",
  })
  .refine(
    (rounds) =>
      rounds.every((round, i) => {
        const previous = rounds[i - 1];
        return previous === undefined || round.startsAt.getTime() >= previous.endsAt.getTime();
      }),
    {
      error: "rounds must follow each other without overlapping",
    },
  )
  .refine((rounds) => rounds.every((round, i) => (i === rounds.length - 1) === (round.qualifiersCount === null)), {
    error: "every round but the last needs a qualifier count; the last one has none",
  })
  .refine(
    (rounds) =>
      rounds.every((round, i) => {
        const previousCut = rounds[i - 1]?.qualifiersCount;
        return previousCut == null || round.qualifiersCount === null || round.qualifiersCount < previousCut;
      }),
    { error: "each cut must be smaller than the one before it" },
  )
  .refine((rounds) => rounds[0]?.format !== ARENA_TOURNAMENT_ROUND_FORMAT.SINGLE_MATCH, {
    error: "the first round must rank on winnings: a single match needs a known roster to allow in",
  })
  .refine(
    (rounds) =>
      rounds.every((round, i) => {
        const previous = rounds[i - 1];
        return (
          round.format !== ARENA_TOURNAMENT_ROUND_FORMAT.SINGLE_MATCH ||
          previous === undefined ||
          round.startsAt.getTime() >= previous.endsAt.getTime() + ARENA_TOURNAMENT_MATCH_MIN_GAP_MS
        );
      }),
    {
      error: `a single match must start at least ${ARENA_TOURNAMENT_MATCH_MIN_GAP_MS / 60_000} minutes after the round before it ends`,
    },
  );

const SaveArenaTournamentBodyObjectSchema = z.object({
  slug: z
    .string()
    .trim()
    .max(ARENA_TOURNAMENT_SLUG_MAX)
    .regex(ARENA_TOURNAMENT_SLUG_REGEX, { error: "lowercase letters, digits and single dashes" })
    // `tournaments/active` is a route and the client's active query key.
    .refine((slug) => slug !== "active", { error: '"active" is reserved' }),
  name: z.string().trim().min(1).max(ARENA_TOURNAMENT_NAME_MAX),
  /** Only users with an ACCEPTED whitelist registration can join. */
  requiresWhitelist: z.boolean(),
  /** Duels a participant must have played to rank on the Sniper board. */
  sniperMinDuels: z.number().int().positive(),
  /** How long after the last round ends admins can still disqualify or void before prizes are credited. */
  payoutGraceMinutes: z.number().int().nonnegative().max(ARENA_TOURNAMENT_MAX_PAYOUT_GRACE_MINUTES),
  rounds: ArenaTournamentRoundsConfigSchema,
  prizeLadder: ArenaTournamentPrizeLadderSchema,
});

/** The deepest rank a tier pays: its last bracket's `rankTo`. */
const deepestPaidRank = (tier: ArenaTournamentPrizeTier): number =>
  tier.places.reduce((deepest, place) => Math.max(deepest, place.rankTo), 0);

/** Body for `POST /v1/cms/arena-tournaments` and `PUT /v1/cms/arena-tournaments/:id`. */
export const SaveArenaTournamentBodySchema = SaveArenaTournamentBodyObjectSchema.refine(
  (body) => {
    // With several rounds, the last one has at most the previous cut's entrants.
    const finalists = body.rounds.at(-2)?.qualifiersCount;
    return finalists == null || body.prizeLadder.every((tier) => deepestPaidRank(tier) <= finalists);
  },
  { error: "a tier pays more places than the last round can have players", path: ["prizeLadder"] },
);
export type SaveArenaTournamentBody = z.infer<typeof SaveArenaTournamentBodySchema>;

export const SaveArenaTournamentResponseSchema = z.object({ id: z.number().int().positive() });
export type SaveArenaTournamentResponse = z.infer<typeof SaveArenaTournamentResponseSchema>;

// ─────────────────────────────── Shared read shapes ───────────────────────────

export const ArenaTournamentPoolDtoSchema = z.object({
  /** Buy-ins of the scored duels so far (see the pool note at the top). */
  volumeUsdt: z.string(),
  /** Index into `ladder` of the tier reached; null below the first threshold. */
  reachedTierIndex: z.number().int().nonnegative().nullable(),
  /** The pool of the reached tier, "0" below the first threshold. */
  currentPoolUsdt: z.string(),
  ladder: z.array(ArenaTournamentPrizeTierSchema),
});
export type ArenaTournamentPoolDto = z.infer<typeof ArenaTournamentPoolDtoSchema>;

export const ArenaTournamentRoundDtoSchema = z.object({
  position: z.number().int().positive(),
  name: z.string(),
  startsAt: z.coerce.date(),
  endsAt: z.coerce.date(),
  format: ArenaTournamentRoundFormatSchema,
  qualifiersCount: z.number().int().positive().nullable(),
  campaignName: z.string().nullable(),
  isLast: z.boolean(),
  status: ArenaTournamentRoundStatusSchema,
  /**
   * Who can score on this round: every non-disqualified participant for round 1, else the
   * previous round's qualifiers (its configured cut until it closes).
   */
  entrantsCount: z.number().int().nonnegative(),
  /** Distinct duels the ranked entrants played in the round; a single match counts once when finalized. */
  duelsPlayed: z.number().int().nonnegative(),
  closedAt: z.coerce.date().nullable(),
});
export type ArenaTournamentRoundDto = z.infer<typeof ArenaTournamentRoundDtoSchema>;

export const ArenaTournamentDtoSchema = z.object({
  slug: z.string(),
  name: z.string(),
  status: ArenaTournamentStatusSchema,
  requiresWhitelist: z.boolean(),
  /** No join step: a player's first counted duel before joining closes enrolls them. */
  enrollsAutomatically: z.boolean(),
  sniperMinDuels: z.number().int().positive(),
  startsAt: z.coerce.date(),
  endsAt: z.coerce.date(),
  /** Joining closes when round 1 ends. */
  joinClosesAt: z.coerce.date(),
  /** When prizes are credited: the last round's end plus the grace window. */
  payoutAt: z.coerce.date(),
  paidAt: z.coerce.date().nullable(),
  participantsCount: z.number().int().nonnegative(),
  /** 1-based position of the live round, or of the next one between rounds; null before start and after the end. */
  currentRoundPosition: z.number().int().positive().nullable(),
  rounds: z.array(ArenaTournamentRoundDtoSchema),
  pool: ArenaTournamentPoolDtoSchema,
});
export type ArenaTournamentDto = z.infer<typeof ArenaTournamentDtoSchema>;

/** One ranked entrant of a round. Live until the round closes, then the frozen result. */
export const ArenaTournamentRoundStandingDtoSchema = z.object({
  rank: z.number().int().positive(),
  hubUserId: z.number().int().positive(),
  nickname: z.string().nullable(),
  /** WINNINGS: gross prizes won (decimal USDT). SINGLE_MATCH: realized + unrealized match PnL, possibly negative. */
  scoreUsdt: z.string(),
  wins: z.number().int().nonnegative(),
  played: z.number().int().nonnegative(),
  /** Inside the cut (non-last rounds only). */
  qualified: z.boolean(),
  /** Last round only: what this rank is paid under the tier reached now (final once paid). */
  prizeUsdt: z.string().nullable(),
});
export type ArenaTournamentRoundStandingDto = z.infer<typeof ArenaTournamentRoundStandingDtoSchema>;

export const ArenaTournamentPageQuerySchema = z.object({
  page: z.coerce.number().int().nonnegative().default(0),
  size: z.coerce.number().int().positive().max(ARENA_TOURNAMENT_PAGE_SIZE_MAX).default(50),
});
export type ArenaTournamentPageQuery = z.infer<typeof ArenaTournamentPageQuerySchema>;

/** Response for `GET .../rounds/:position/leaderboard`. Page is 0-indexed. */
export const ArenaTournamentRoundLeaderboardResponseSchema = z.object({
  round: ArenaTournamentRoundDtoSchema,
  items: z.array(ArenaTournamentRoundStandingDtoSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().nonnegative(),
  size: z.number().int().positive(),
  /** True once the round's result is frozen. */
  frozen: z.boolean(),
});
export type ArenaTournamentRoundLeaderboardResponse = z.infer<typeof ArenaTournamentRoundLeaderboardResponseSchema>;

export const ArenaTournamentSniperStandingDtoSchema = z.object({
  rank: z.number().int().positive(),
  hubUserId: z.number().int().positive(),
  nickname: z.string().nullable(),
  /** wins / played, a decimal string in [0, 1]. */
  winRate: z.string(),
  wins: z.number().int().nonnegative(),
  played: z.number().int().nonnegative(),
  /** What this rank is paid under the tier reached now: the prize on an eligible rank 1, else "0". */
  prizeUsdt: z.string(),
});
export type ArenaTournamentSniperStandingDto = z.infer<typeof ArenaTournamentSniperStandingDtoSchema>;

export const ArenaTournamentStreakStandingDtoSchema = z.object({
  rank: z.number().int().positive(),
  hubUserId: z.number().int().positive(),
  nickname: z.string().nullable(),
  longestStreak: z.number().int().positive(),
  /** What this rank is paid under the tier reached now: the prize on rank 1, else "0". */
  prizeUsdt: z.string(),
});
export type ArenaTournamentStreakStandingDto = z.infer<typeof ArenaTournamentStreakStandingDtoSchema>;

/** Response for `GET .../side-boards/SNIPER`. */
export const ArenaTournamentSniperBoardResponseSchema = z.object({
  board: z.literal(ARENA_TOURNAMENT_SIDE_BOARD.SNIPER),
  /** Rank 1's prize under the tier reached now. */
  prizeUsdt: z.string(),
  minDuels: z.number().int().positive(),
  /** Traders under `minDuels` are listed too, after the eligible ones, and win nothing. */
  items: z.array(ArenaTournamentSniperStandingDtoSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().nonnegative(),
  size: z.number().int().positive(),
});
export type ArenaTournamentSniperBoardResponse = z.infer<typeof ArenaTournamentSniperBoardResponseSchema>;

/** Response for `GET .../side-boards/STREAK`. */
export const ArenaTournamentStreakBoardResponseSchema = z.object({
  board: z.literal(ARENA_TOURNAMENT_SIDE_BOARD.STREAK),
  prizeUsdt: z.string(),
  items: z.array(ArenaTournamentStreakStandingDtoSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().nonnegative(),
  size: z.number().int().positive(),
});
export type ArenaTournamentStreakBoardResponse = z.infer<typeof ArenaTournamentStreakBoardResponseSchema>;

// ─────────────────────────────── Arena-facing ─────────────────────────────────

/** Response for `GET /v1/cede-arena/tournaments/active`: what the lobby banner needs. */
export const ArenaActiveTournamentResponseSchema = z.object({
  tournament: z
    .object({
      slug: z.string(),
      name: z.string(),
      status: ArenaTournamentStatusSchema,
      startsAt: z.coerce.date(),
      joinClosesAt: z.coerce.date(),
      enrollsAutomatically: z.boolean(),
      currentPoolUsdt: z.string(),
    })
    .nullable(),
});
export type ArenaActiveTournamentResponse = z.infer<typeof ArenaActiveTournamentResponseSchema>;

/** Response for `GET /v1/cede-arena/tournaments/current`: where the nav's Tournament link lands. */
export const ArenaCurrentTournamentResponseSchema = z.object({ slug: z.string().nullable() });
export type ArenaCurrentTournamentResponse = z.infer<typeof ArenaCurrentTournamentResponseSchema>;

export const ARENA_TOURNAMENT_JOIN_BLOCKER = {
  NOT_WHITELISTED: "NOT_WHITELISTED",
  JOIN_CLOSED: "JOIN_CLOSED",
  DISQUALIFIED: "DISQUALIFIED",
} as const;
export type ARENA_TOURNAMENT_JOIN_BLOCKER =
  (typeof ARENA_TOURNAMENT_JOIN_BLOCKER)[keyof typeof ARENA_TOURNAMENT_JOIN_BLOCKER];
export const ArenaTournamentJoinBlockerSchema = z.enum([
  ARENA_TOURNAMENT_JOIN_BLOCKER.NOT_WHITELISTED,
  ARENA_TOURNAMENT_JOIN_BLOCKER.JOIN_CLOSED,
  ARENA_TOURNAMENT_JOIN_BLOCKER.DISQUALIFIED,
]);

export const ARENA_TOURNAMENT_MY_ROUND_STATE = {
  /** Can score on the round (it has not closed yet). */
  IN_PLAY: "IN_PLAY",
  QUALIFIED: "QUALIFIED",
  ELIMINATED: "ELIMINATED",
  /** Played the closed last round; `prizeUsdt` says what they won, if anything. */
  FINISHED: "FINISHED",
  /** Not an entrant: out before this round, or never joined. */
  NOT_ENTERED: "NOT_ENTERED",
} as const;
export type ARENA_TOURNAMENT_MY_ROUND_STATE =
  (typeof ARENA_TOURNAMENT_MY_ROUND_STATE)[keyof typeof ARENA_TOURNAMENT_MY_ROUND_STATE];
export const ArenaTournamentMyRoundStateSchema = z.enum([
  ARENA_TOURNAMENT_MY_ROUND_STATE.IN_PLAY,
  ARENA_TOURNAMENT_MY_ROUND_STATE.QUALIFIED,
  ARENA_TOURNAMENT_MY_ROUND_STATE.ELIMINATED,
  ARENA_TOURNAMENT_MY_ROUND_STATE.FINISHED,
  ARENA_TOURNAMENT_MY_ROUND_STATE.NOT_ENTERED,
]);

export const ArenaTournamentMyRoundDtoSchema = z.object({
  position: z.number().int().positive(),
  state: ArenaTournamentMyRoundStateSchema,
  rank: z.number().int().positive().nullable(),
  scoreUsdt: z.string().nullable(),
  /** Non-last rounds, while ranked: score minus the last qualifying score (negative = below the cut). */
  gapToCutUsdt: z.string().nullable(),
  /** Last round only: what the current rank pays under the tier reached now. */
  prizeUsdt: z.string().nullable(),
});
export type ArenaTournamentMyRoundDto = z.infer<typeof ArenaTournamentMyRoundDtoSchema>;

/** Response for `GET /v1/cede-arena/tournaments/:slug/me` and `POST .../join`. */
export const ArenaTournamentMeResponseSchema = z.object({
  /** A participant: joined, or enrolled by a counted duel (see `enrollsAutomatically`). */
  joined: z.boolean(),
  /** When they joined, or settled their first counted duel. */
  joinedAt: z.coerce.date().nullable(),
  /** Why the user cannot join right now; null when they can, or already joined. */
  joinBlocker: ArenaTournamentJoinBlockerSchema.nullable(),
  disqualified: z.boolean(),
  rounds: z.array(ArenaTournamentMyRoundDtoSchema),
  sniper: z.object({
    rank: z.number().int().positive().nullable(),
    winRate: z.string(),
    wins: z.number().int().nonnegative(),
    played: z.number().int().nonnegative(),
    duelsToEligibility: z.number().int().nonnegative(),
  }),
  streak: z.object({
    rank: z.number().int().positive().nullable(),
    longestStreak: z.number().int().nonnegative(),
    /** The leader's streak minus this user's; 0 when leading or nobody has one. */
    behindLeader: z.number().int().nonnegative(),
  }),
});
export type ArenaTournamentMeResponse = z.infer<typeof ArenaTournamentMeResponseSchema>;

// ─────────────────────────────── CMS reads & actions ──────────────────────────

export const CmsArenaTournamentListItemDtoSchema = z.object({
  id: z.number().int().positive(),
  slug: z.string(),
  name: z.string(),
  status: ArenaTournamentStatusSchema,
  startsAt: z.coerce.date(),
  endsAt: z.coerce.date(),
  participantsCount: z.number().int().nonnegative(),
  paidAt: z.coerce.date().nullable(),
});
export type CmsArenaTournamentListItemDto = z.infer<typeof CmsArenaTournamentListItemDtoSchema>;

export const ListCmsArenaTournamentsResponseSchema = z.object({
  items: z.array(CmsArenaTournamentListItemDtoSchema),
});
export type ListCmsArenaTournamentsResponse = z.infer<typeof ListCmsArenaTournamentsResponseSchema>;

export const CmsArenaTournamentPayoutDtoSchema = z.object({
  hubUserId: z.number().int().positive(),
  nickname: z.string().nullable(),
  category: ArenaTournamentPrizeCategorySchema,
  rank: z.number().int().positive(),
  amountUsdt: z.string(),
  creditedAt: z.coerce.date().nullable(),
});
export type CmsArenaTournamentPayoutDto = z.infer<typeof CmsArenaTournamentPayoutDtoSchema>;

export const CmsArenaTournamentRoundDtoSchema = ArenaTournamentRoundDtoSchema.extend({
  /** When the round's results were frozen; null until then. A frozen round not yet closed is still closing. */
  frozenAt: z.coerce.date().nullable(),
});
export type CmsArenaTournamentRoundDto = z.infer<typeof CmsArenaTournamentRoundDtoSchema>;

/** Response for `GET /v1/cms/arena-tournaments/:id`. */
export const CmsArenaTournamentDtoSchema = SaveArenaTournamentBodyObjectSchema.omit({
  rounds: true,
  prizeLadder: true,
}).extend({
  id: z.number().int().positive(),
  tournament: ArenaTournamentDtoSchema.extend({ rounds: z.array(CmsArenaTournamentRoundDtoSchema) }),
  /** Frozen once the payout ran; empty before. */
  payouts: z.array(CmsArenaTournamentPayoutDtoSchema),
  /** Prizes of the paid tier nobody could take (fewer finalists than paid places, empty side board). */
  undistributedUsdt: z.string().nullable(),
});
export type CmsArenaTournamentDto = z.infer<typeof CmsArenaTournamentDtoSchema>;

export const CmsArenaTournamentParticipantsQuerySchema = ArenaTournamentPageQuerySchema.extend({
  /** Matches a nickname (case-insensitive substring) or an exact hub user id. */
  search: z.string().trim().max(ARENA_TOURNAMENT_PARTICIPANT_SEARCH_MAX).optional(),
  disqualifiedOnly: z
    .enum(["true", "false"])
    .transform((value) => value === "true")
    .optional(),
});
export type CmsArenaTournamentParticipantsQuery = z.infer<typeof CmsArenaTournamentParticipantsQuerySchema>;

export const CmsArenaTournamentParticipantDtoSchema = z.object({
  hubUserId: z.number().int().positive(),
  nickname: z.string().nullable(),
  joinedAt: z.coerce.date(),
  disqualifiedAt: z.coerce.date().nullable(),
  disqualifiedReason: z.string().nullable(),
  /** Highest round position the participant is an entrant of. */
  reachedRoundPosition: z.number().int().positive(),
  played: z.number().int().nonnegative(),
  wins: z.number().int().nonnegative(),
  longestStreak: z.number().int().nonnegative(),
  buyInsUsdt: z.string(),
});
export type CmsArenaTournamentParticipantDto = z.infer<typeof CmsArenaTournamentParticipantDtoSchema>;

export const ListCmsArenaTournamentParticipantsResponseSchema = z.object({
  items: z.array(CmsArenaTournamentParticipantDtoSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().nonnegative(),
  size: z.number().int().positive(),
});
export type ListCmsArenaTournamentParticipantsResponse = z.infer<
  typeof ListCmsArenaTournamentParticipantsResponseSchema
>;

/** One duel of a participant inside the tournament window, counted or voided. */
export const CmsArenaTournamentDuelDtoSchema = z.object({
  campaignName: z.string(),
  epochId: z.number().int().nonnegative(),
  finalizedAt: z.coerce.date(),
  buyInUsdt: z.string(),
  isSpinAndGo: z.boolean(),
  won: z.boolean(),
  prizeUsdt: z.string(),
  /** Won, but the prize credit has not landed yet: `prizeUsdt` reads 0 until it does. */
  awaitingPrizeCredit: z.boolean(),
  opponents: z.array(z.object({ hubUserId: z.number().int().positive(), nickname: z.string().nullable() })),
  voided: z.boolean(),
});
export type CmsArenaTournamentDuelDto = z.infer<typeof CmsArenaTournamentDuelDtoSchema>;

export const ListCmsArenaTournamentDuelsResponseSchema = z.object({
  items: z.array(CmsArenaTournamentDuelDtoSchema),
});
export type ListCmsArenaTournamentDuelsResponse = z.infer<typeof ListCmsArenaTournamentDuelsResponseSchema>;

export const CmsArenaTournamentVoidedDuelDtoSchema = z.object({
  campaignName: z.string(),
  epochId: z.number().int().nonnegative(),
  reason: z.string(),
  voidedAt: z.coerce.date(),
});
export type CmsArenaTournamentVoidedDuelDto = z.infer<typeof CmsArenaTournamentVoidedDuelDtoSchema>;

export const ListCmsArenaTournamentVoidedDuelsResponseSchema = z.object({
  items: z.array(CmsArenaTournamentVoidedDuelDtoSchema),
});
export type ListCmsArenaTournamentVoidedDuelsResponse = z.infer<typeof ListCmsArenaTournamentVoidedDuelsResponseSchema>;

/** Body for `POST /v1/cms/arena-tournaments/:id/participants/disqualify`. */
export const DisqualifyArenaTournamentParticipantBodySchema = z.object({
  hubUserId: z.number().int().positive(),
  reason: z.string().trim().min(1).max(ARENA_TOURNAMENT_DISQUALIFY_REASON_MAX),
});
export type DisqualifyArenaTournamentParticipantBody = z.infer<typeof DisqualifyArenaTournamentParticipantBodySchema>;

/** Body for `POST /v1/cms/arena-tournaments/:id/participants/reinstate`. */
export const ReinstateArenaTournamentParticipantBodySchema = z.object({
  hubUserId: z.number().int().positive(),
});
export type ReinstateArenaTournamentParticipantBody = z.infer<typeof ReinstateArenaTournamentParticipantBodySchema>;

/** Body for `POST /v1/cms/arena-tournaments/:id/voided-duels`. */
export const VoidArenaTournamentDuelBodySchema = z.object({
  campaignName: z.string().trim().min(1),
  epochId: z.number().int().nonnegative(),
  reason: z.string().trim().min(1).max(ARENA_TOURNAMENT_VOID_REASON_MAX),
});
export type VoidArenaTournamentDuelBody = z.infer<typeof VoidArenaTournamentDuelBodySchema>;

/** Body for `POST /v1/cms/arena-tournaments/:id/voided-duels/restore`. */
export const RestoreArenaTournamentDuelBodySchema = z.object({
  campaignName: z.string().trim().min(1),
  epochId: z.number().int().nonnegative(),
});
export type RestoreArenaTournamentDuelBody = z.infer<typeof RestoreArenaTournamentDuelBodySchema>;
